// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf editor's panels (frontend-aev .5 .6 .7 .14): Sessioner og
 * blokke with the suggested splits (left), Flyt blokke with the gizmo's
 * numbers and apply, and Historik with undo / redo, restore and compare
 * (right), plus the box-select overlay over the canvas. State lives in
 * `usePosegrafEditor`; these only render it.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import type { PoseBlock } from '../../api/types';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { SegmentedControl } from '../../components/SegmentedControl';
import { TextInput } from '../../components/TextInput';
import { TextLink } from '../../components/TextLink';
import { REASON_LABEL, sessionColorToken, sessionsOf, suggestionText, type ScreenRect } from '../../posegraf/rig';
import { parseNumberField, type GizmoDelta } from '../../posegraf/snap';
import { canUndo, diffByBlock, diffSummary, historyRows, redoTarget, versionFacts, versionKindLabel } from '../../posegraf/versions';
import type { GizmoMode, PivotMode, PosegrafEditor } from './usePosegrafEditor';
import styles from './EditorPanels.module.css';

const fmt = (v: number, d = 2) => v.toLocaleString('da-DK', { maximumFractionDigits: d, minimumFractionDigits: d });
const range = (b: Pick<PoseBlock, 'first_node' | 'last_node'>) => `${b.first_node}–${b.last_node}`;

// ------------------------------------------------------------------ left --

export function BlocksPanel({
  editor,
  canEdit,
  onImport,
  onFocus,
}: {
  editor: PosegrafEditor;
  canEdit: boolean;
  onImport: () => void;
  onFocus: (node: number) => void;
}) {
  const { blocks, selection, openSuggestions: open, suggestions } = editor;
  const sessions = sessionsOf(blocks);
  const [renaming, setRenaming] = useState<number | null>(null);
  const glitches = suggestions?.glitches.length ?? 0;

  return (
    <>
      <section className={styles.group} aria-labelledby="pg-sessions">
        <h2 id="pg-sessions" className={styles.heading}>
          Sessioner og blokke
        </h2>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onImport} className={styles.wide}>
            Importér RTAB-Map-sessioner…
          </Button>
        )}
        {editor.loading && <p className={styles.muted}>Blokkene indlæses…</p>}
        {!editor.loading && sessions.length === 0 && (
          <p className={styles.muted}>Ingen sessioner med poser endnu. Importér RTAB-Map-sessioner for at begynde.</p>
        )}
        {sessions.map((s) => (
          <div key={s.session} className={styles.session}>
            <div className={styles.sessionHead}>
              <span className={styles.swatch} style={{ background: `var(${sessionColorToken(s.session)})` }} aria-hidden="true" />
              <span className={styles.sessionName}>s{s.session}</span>
              <span className={styles.muted}>
                scan {s.scanId} · {s.frames.toLocaleString('da-DK')} billeder
              </span>
            </div>
            <ul className={styles.blockList}>
              {s.blocks.map((b) => {
                const on = selection.has(b.first_node);
                return (
                  <li key={b.first_node} className={`${styles.blockRow}${on ? ` ${styles.blockOn}` : ''}`}>
                    {renaming === b.id ? (
                      <RenameField
                        initial={b.name}
                        onDone={(name) => {
                          setRenaming(null);
                          if (name && name !== b.name) void editor.actions.rename(b, name);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className={styles.blockButton}
                        aria-pressed={on}
                        onClick={(e) =>
                          editor.setSelection(
                            e.shiftKey
                              ? new Set(on ? [...selection].filter((k) => k !== b.first_node) : [...selection, b.first_node])
                              : new Set([b.first_node]),
                          )
                        }
                        onDoubleClick={() => canEdit && setRenaming(b.id)}
                        title="Klik vælger, shift-klik tilføjer, dobbeltklik omdøber"
                      >
                        <span className={styles.blockName}>{b.name}</span>
                        {b.detached && (
                          <span className={styles.badgeWarn} title="Ingen odometri til blokken før — en løkke skal holde den på plads">
                            frakoblet
                          </span>
                        )}
                        <span className={`${styles.blockMeta} mono`}>
                          {range(b)} · {b.frame_count.toLocaleString('da-DK')}
                        </span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {canEdit && editor.selBlocks.length > 0 && (
          <div className={styles.rowActions}>
            {editor.selBlocks.length === 1 && (
              <>
                <TextLink size="sm" onClick={() => setRenaming(editor.selBlocks[0].id)}>
                  Omdøb
                </TextLink>
                {(() => {
                  const b = editor.selBlocks[0];
                  const first = blocks.every((x) => x.first_node >= b.first_node);
                  if (first) return null;
                  return (
                    <TextLink size="sm" disabled={editor.busy} onClick={() => void editor.actions.detach(b, b.detached)}>
                      {b.detached ? 'Kobl til igen' : 'Frakobl'}
                    </TextLink>
                  );
                })()}
              </>
            )}
            {editor.mergeable && (
              <TextLink size="sm" disabled={editor.busy} onClick={editor.actions.merge}>
                Flet «{editor.mergeable[0].name}» og «{editor.mergeable[1].name}»
              </TextLink>
            )}
          </div>
        )}
      </section>

      {canEdit && (
        <section className={styles.group} aria-label="Værktøjer">
          <div className={styles.toggles}>
            <Button
              variant={editor.addSplit ? 'primary' : 'outline'}
              size="sm"
              aria-pressed={editor.addSplit}
              onClick={() => editor.setAddSplit(!editor.addSplit)}
              title="Klik et kamera på banen for at begynde en ny blok dér"
            >
              Del ved klik
            </Button>
            <Button
              variant={editor.boxMode ? 'primary' : 'outline'}
              size="sm"
              aria-pressed={editor.boxMode}
              onClick={() => editor.setBoxMode(!editor.boxMode)}
              title="Træk en boks på planen (B); shift tilføjer"
            >
              Boksmarkér
            </Button>
          </div>
          {editor.addSplit && <p className={styles.hint}>Klik et kamera på banen: en ny blok begynder dér.</p>}
        </section>
      )}

      <section className={styles.group} aria-labelledby="pg-suggest">
        <div className={styles.groupHead}>
          <h2 id="pg-suggest" className={styles.heading}>
            Foreslåede delinger
          </h2>
          <span className={`${styles.muted} mono`}>{open.length}</span>
        </div>
        {canEdit && open.length > 1 && (
          <TextLink size="sm" disabled={editor.busy} onClick={() => void editor.actions.acceptAll()}>
            Acceptér alle forslag
          </TextLink>
        )}
        {open.length === 0 && <p className={styles.muted}>Ingen åbne forslag.</p>}
        <ol className={styles.suggestions}>
          {open.map((s) => {
            const on = editor.focusedSuggestion === s.node_id;
            return (
              <li key={s.node_id} className={`${styles.suggestion}${on ? ` ${styles.suggestionOn}` : ''}`}>
                <button type="button" className={styles.suggestionMain} onClick={() => onFocus(s.node_id)} title={s.detail}>
                  <span className={`${styles.reason} ${styles[`reason_${s.reason}`] ?? ''}`}>{REASON_LABEL[s.reason]}</span>
                  <span className="mono">{s.node_id}</span>
                  <span className={styles.suggestionText}>{suggestionText(s)}</span>
                  <span className={styles.score} aria-label={`Styrke ${Math.round(s.score * 100)} %`}>
                    <span className={styles.scoreFill} style={{ inlineSize: `${Math.round(s.score * 100)}%` }} />
                  </span>
                </button>
                {canEdit && (
                  <span className={styles.suggestionActions}>
                    <TextLink size="sm" disabled={editor.busy} onClick={() => void editor.actions.acceptSuggestion(s.node_id)}>
                      Acceptér
                    </TextLink>
                    <TextLink size="sm" onClick={() => editor.actions.ignoreSuggestion(s.node_id)}>
                      Ignorér
                    </TextLink>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        {glitches > 0 && (
          <p className={styles.hint}>
            {glitches} enkeltbilleder springer væk og tilbage (fejlmålinger) — de deler ikke banen.
          </p>
        )}
      </section>
    </>
  );
}

function RenameField({ initial, onDone }: { initial: string; onDone: (name: string | null) => void }) {
  const [draft, setDraft] = useState(initial);
  return (
    <TextInput
      size="sm"
      autoFocus
      value={draft}
      aria-label="Blokkens navn"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onDone(draft.trim() || null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onDone(draft.trim() || null);
        if (e.key === 'Escape') onDone(null);
      }}
    />
  );
}

// ----------------------------------------------------------------- right --

const GIZMO_MODES: readonly { value: GizmoMode; label: string }[] = [
  { value: 'translate', label: 'Flyt (G)' },
  { value: 'rotate', label: 'Drej (R)' },
];
const PIVOTS: readonly { value: PivotMode; label: string }[] = [
  { value: 'centroid', label: 'Tyngdepunkt' },
  { value: 'joint', label: 'Led' },
  { value: 'picked', label: 'Valgt punkt' },
];
const APPLY_MODES: readonly { value: 'reseed' | 'local'; label: string }[] = [
  { value: 'reseed', label: 'Nyt udgangspunkt' },
  { value: 'local', label: 'Kun valgte' },
];
const FIELDS: readonly { key: keyof GizmoDelta; label: string; unit: string }[] = [
  { key: 'dx', label: 'Δx', unit: 'm' },
  { key: 'dy', label: 'Δy', unit: 'm' },
  { key: 'dz', label: 'Δz', unit: 'm' },
  { key: 'yaw', label: 'Yaw', unit: '°' },
  { key: 'pitch', label: 'Pitch', unit: '°' },
  { key: 'roll', label: 'Roll', unit: '°' },
];

export function TransformPanel({ editor, canEdit }: { editor: PosegrafEditor; canEdit: boolean }) {
  const { selBlocks, delta, preview } = editor;
  const frames = selBlocks.reduce((n, b) => n + b.frame_count, 0);
  const moved = Object.values(delta).some((v) => v !== 0);
  return (
    <section className={styles.group} aria-labelledby="pg-move">
      <h2 id="pg-move" className={styles.heading}>
        Flyt blokke
      </h2>
      {selBlocks.length === 0 ? (
        <p className={styles.muted}>
          Klik en blok på banen. Shift-klik tilføjer, ctrl-klik på et led vælger kæden til sessionens ende, B trækker en boks. Esc rydder.
        </p>
      ) : (
        <p className={styles.selection}>
          <strong>{selBlocks.length === 1 ? selBlocks[0].name : `${selBlocks.length} blokke`}</strong>
          <span className={styles.muted}>
            {' '}
            · {frames.toLocaleString('da-DK')} billeder
            {selBlocks.length > 1 ? ` · ${selBlocks.map((b) => b.name).join(', ')}` : ` · ${range(selBlocks[0])}`}
          </span>
        </p>
      )}
      {canEdit && selBlocks.length > 0 && (
        <>
          <div className={styles.controlRow}>
            <SegmentedControl aria-label="Håndtag" items={GIZMO_MODES} value={editor.gizmoMode} onChange={editor.setGizmoMode} />
          </div>
          <span className={styles.label}>Omdrejningspunkt</span>
          <SegmentedControl
            aria-label="Omdrejningspunkt"
            items={PIVOTS.map((p) => (p.value === 'joint' ? { ...p, disabled: !editor.hasJoint } : p))}
            value={editor.pivotMode}
            onChange={(v) => {
              editor.setPivotMode(v);
              if (v === 'picked') editor.setPickingPivot(true);
            }}
          />
          {editor.pickingPivot && <p className={styles.hint}>Klik et kamera, et led eller et punkt i skyen.</p>}
          <Checkbox
            label="Fang"
            hint={<span className={styles.muted} title="yaw i 90°-trin (shift: 1°), z til etagerne, xy til 5 cm">90° · etager · 5 cm</span>}
            checked={editor.snapping}
            onChange={(e) => editor.setSnapping(e.target.checked)}
          />
          <div className={styles.fields}>
            {FIELDS.map((f) => (
              <DeltaField
                key={`${f.key}:${delta[f.key]}`}
                label={f.label}
                unit={f.unit}
                value={delta[f.key]}
                onCommit={(v) => editor.setDelta({ ...delta, [f.key]: v })}
              />
            ))}
          </div>
          <span className={styles.label}>Anvend som</span>
          <SegmentedControl
            aria-label="Anvend som"
            items={APPLY_MODES}
            value={editor.applyOpts.mode}
            onChange={(mode) => editor.setApplyOpts({ ...editor.applyOpts, mode })}
          />
          <div className={styles.controlRow}>
            <label className={styles.inline}>
              <span className={styles.label}>Blød overgang</span>
              <TextInput
                size="sm"
                mono
                inputMode="numeric"
                className={styles.small}
                value={String(editor.applyOpts.blendFrames)}
                suffix="billeder"
                onChange={(e) => {
                  const v = parseNumberField(e.target.value);
                  if (v !== null && v >= 0) editor.setApplyOpts({ ...editor.applyOpts, blendFrames: Math.round(v) });
                }}
              />
            </label>
            <Checkbox label="Hold blokken" checked={editor.applyOpts.hold} onChange={(e) => editor.setApplyOpts({ ...editor.applyOpts, hold: e.target.checked })} />
          </div>

          {moved && (
            <div className={styles.preview} aria-live="polite">
              {editor.dragging ? (
                <span className={styles.muted}>Slip for at se overgangene…</span>
              ) : editor.previewing && !preview ? (
                <span className={styles.muted}>Beregner overgangene…</span>
              ) : preview ? (
                <>
                  <span>
                    {preview.frames_moved.toLocaleString('da-DK')} billeder flyttes, {preview.frames_blended} i overgange, højst {fmt(preview.max_shift_m)} m
                  </span>
                  {preview.joints.length > 0 && (
                    <table className={styles.joints}>
                      <thead>
                        <tr>
                          <th scope="col">Led</th>
                          <th scope="col">Afstand før</th>
                          <th scope="col">efter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.joints.map((j) => (
                          <tr key={`${j.inside_node}-${j.outside_node}`}>
                            <td className="mono">
                              {j.outside_node || '—'}|{j.inside_node}
                              {j.session_boundary ? ' session' : j.detached ? ' frakoblet' : ''}
                            </td>
                            <td className="mono">{fmt(j.gap_before_m)} m</td>
                            <td className={`mono ${j.gap_after_m > j.gap_before_m + 0.05 ? styles.worse : ''}`}>{fmt(j.gap_after_m)} m</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {preview.loops_disagreeing > 0 && (
                    <span className={styles.warn}>
                      {preview.loops_disagreeing} af {preview.loops_touching} løkker til blokken bliver uenige (&gt; 0,3 m / 4°).
                    </span>
                  )}
                </>
              ) : null}
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="primary" size="sm" disabled={!moved || editor.busy || !editor.pivot} onClick={editor.actions.apply}>
              Anvend
            </Button>
            <Button variant="outline" size="sm" disabled={!moved} onClick={editor.actions.reset}>
              Nulstil
            </Button>
          </div>
        </>
      )}
      {canEdit && (
        <TextLink size="sm" disabled={editor.busy} onClick={() => void editor.actions.rebuildOdometry()}>
          Genopbyg odometri fra startposerne
        </TextLink>
      )}
    </section>
  );
}

function DeltaField({ label, unit, value, onCommit }: { label: string; unit: string; value: number; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState(value === 0 ? '0' : value.toLocaleString('da-DK', { maximumFractionDigits: 3 }));
  const commit = () => {
    const v = parseNumberField(draft);
    if (v !== null && v !== value) onCommit(v);
  };
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <TextInput
        size="sm"
        mono
        inputMode="decimal"
        value={draft}
        suffix={unit}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
    </label>
  );
}

export function HistoryPanel({ editor, canEdit }: { editor: PosegrafEditor; canEdit: boolean }) {
  const { versions, diff } = editor;
  const rows = historyRows(versions);
  const redo = redoTarget(versions);
  const current = versions?.current ?? null;
  const diffRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (diff) diffRef.current?.scrollIntoView({ block: 'nearest' });
  }, [diff]);
  return (
    <section className={styles.group} aria-labelledby="pg-history">
      <div className={styles.groupHead}>
        <h2 id="pg-history" className={styles.heading}>
          Historik
        </h2>
        {canEdit && (
          <span className={styles.undo}>
            <Button variant="outline" size="sm" disabled={!canUndo(versions) || editor.busy} onClick={() => void editor.actions.undo()} title="Fortryd (Ctrl+Z)">
              Fortryd
            </Button>
            <Button variant="outline" size="sm" disabled={!redo || editor.busy} onClick={() => void editor.actions.redo()} title="Gentag (Ctrl+Shift+Z)">
              Gentag
            </Button>
          </span>
        )}
      </div>
      {rows.length === 0 && <p className={styles.muted}>Ingen versioner endnu.</p>}
      <ol className={styles.versions}>
        {rows.map(({ version: v, onBranch }) => {
          const isCurrent = v.id === current;
          const facts = versionFacts(v);
          return (
            <li key={v.id} className={`${styles.version}${isCurrent ? ` ${styles.versionOn}` : ''}${onBranch ? '' : ` ${styles.offBranch}`}`}>
              <div className={styles.versionHead}>
                <span className="mono">#{v.id}</span>
                <span className={styles.kind}>{versionKindLabel(v.kind)}</span>
                {isCurrent && <span className={styles.badgeNow}>nu</span>}
                <span className={`${styles.time} mono`}>{new Date(v.created_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <span className={styles.versionLabel} title={v.label}>
                {v.label}
              </span>
              {facts.length > 0 && <span className={styles.muted}>{facts.join(' · ')}</span>}
              <span className={styles.versionActions}>
                {current !== null && !isCurrent && (
                  <TextLink size="sm" onClick={() => void editor.compare(v.id, current)}>
                    Sammenlign med nu
                  </TextLink>
                )}
                {canEdit && !isCurrent && (
                  <TextLink size="sm" disabled={editor.busy} onClick={() => void editor.actions.adopt(v.id)}>
                    Gendan
                  </TextLink>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {diff && (
        <div ref={diffRef} className={styles.diff} aria-live="polite">
          <div className={styles.groupHead}>
            <strong>
              #{diff.from} → #{diff.to}
            </strong>
            <TextLink size="sm" onClick={() => editor.setDiff(null)}>
              Luk
            </TextLink>
          </div>
          <span>{diffSummary(diff)}</span>
          <table className={styles.joints}>
            <thead>
              <tr>
                <th scope="col">Blok</th>
                <th scope="col">Billeder</th>
                <th scope="col">Højst</th>
              </tr>
            </thead>
            <tbody>
              {diffByBlock(diff, editor.blocks)
                .slice(0, 6)
                .map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td className="mono">{r.frames.toLocaleString('da-DK')}</td>
                    <td className="mono">
                      {fmt(r.maxShiftM)} m / {fmt(r.maxRotationDeg, 1)}°
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------ box select --

/** Over the canvas while box-select is on: drag a rectangle; shift adds. */
export function BoxSelectOverlay({ onBox }: { onBox: (rect: ScreenRect, mods: { shift: boolean }) => void }) {
  const [rect, setRect] = useState<ScreenRect | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const b = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };
  return (
    <div
      className={styles.boxLayer}
      role="presentation"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = at(e);
        setRect({ x0: start.current.x, y0: start.current.y, x1: start.current.x, y1: start.current.y });
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const p = at(e);
        setRect({ x0: start.current.x, y0: start.current.y, x1: p.x, y1: p.y });
      }}
      onPointerUp={(e) => {
        if (start.current && rect) onBox(rect, { shift: e.shiftKey });
        start.current = null;
        setRect(null);
      }}
    >
      {rect && (
        <div
          className={styles.box}
          style={{
            insetInlineStart: Math.min(rect.x0, rect.x1),
            insetBlockStart: Math.min(rect.y0, rect.y1),
            inlineSize: Math.abs(rect.x1 - rect.x0),
            blockSize: Math.abs(rect.y1 - rect.y0),
          }}
        />
      )}
    </div>
  );
}
